import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { config } from '../config';
import { 
  User, 
  Phone, 
  Mail, 
  Lock, 
  CheckCircle, 
  XCircle,
  Eye,
  EyeOff
} from 'lucide-react';

const apiUrl = config.apiUrl;

const Register = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        password: '',
        password_confirmation: ''
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error when user types
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'নাম প্রয়োজন';
        }

        if (!formData.phone.trim()) {
            newErrors.phone = 'ফোন নম্বর প্রয়োজন';
        } else if (!/^[0-9+]{11,15}$/.test(formData.phone)) {
            newErrors.phone = 'সঠিক ফোন নম্বর দিন';
        }

        if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'সঠিক ইমেইল দিন';
        }

        if (!formData.password) {
            newErrors.password = 'পাসওয়ার্ড প্রয়োজন';
        } else if (formData.password.length < 8) {
            newErrors.password = 'পাসওয়ার্ড最少 ৮ অক্ষর হতে হবে';
        }

        if (formData.password !== formData.password_confirmation) {
            newErrors.password_confirmation = 'পাসওয়ার্ড মেলে না';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        setLoading(true);
        setErrors({});

        try {
            const response = await axios.post(`${apiUrl}/register`, formData);
            
            console.log('Registration response:', response.data); // Debugging

            // Check for both success and token in response
            if (response.data.success && response.data.token) {
                // Store token in localStorage
                localStorage.setItem('token', response.data.token);
                localStorage.setItem('user', JSON.stringify(response.data.user));
                
                alert("রেজিস্ট্রেশন সফল হয়েছে! স্বয়ংক্রিয়ভাবে লগইন করা হয়েছে।");
                navigate('/dashboard');
            } else {
                // If success is false or token is missing
                setErrors({ 
                    submit: response.data.message || 'রেজিস্ট্রেশন সম্পূর্ণ হয়নি। অনুগ্রহ করে আবার চেষ্টা করুন।' 
                });
            }
        } catch (error) {
            console.error('Registration error:', error);
            
            // More detailed error handling
            if (error.response?.status === 422) {
                // Validation errors from Laravel
                setErrors(error.response.data.errors || {});
            } else if (error.response?.data?.message) {
                setErrors({ submit: error.response.data.message });
            } else if (error.message) {
                setErrors({ submit: error.message });
            } else {
                setErrors({ submit: 'রেজিস্ট্রেশন ব্যর্থ হয়েছে। সার্ভার সমস্যা হতে পারে।' });
            }
        } finally {
            setLoading(false);
        }
    };

    const checkPhoneAvailability = async () => {
        if (!formData.phone || errors.phone) return;

        try {
            const response = await axios.post(`${apiUrl}/check-phone`, {
                phone: formData.phone
            });

            if (!response.data.available) {
                setErrors(prev => ({
                    ...prev,
                    phone: 'এই ফোন নম্বরটি ইতিমধ্যে নিবন্ধিত'
                }));
            }
        } catch (error) {
            console.error('Phone check error:', error);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-8 px-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-center">
                    <h1 className="text-2xl font-bold text-white">একাউন্ট তৈরি করুন</h1>
                    <p className="text-blue-100 mt-2">আপনার প্রশ্ন ব্যাংক একাউন্ট তৈরি করুন</p>
                </div>

                <div className="p-6">
                    {/* Error Message */}
                    {errors.submit && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center">
                            <XCircle className="h-5 w-5 text-red-600 mr-2" />
                            <span className="text-red-800 text-sm">{errors.submit}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Name Field */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                                <User className="h-4 w-4 mr-2 text-blue-600" />
                                পুরো নাম *
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                                    errors.name ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder="আপনার পুরো নাম লিখুন"
                                required
                            />
                            {errors.name && (
                                <p className="mt-1 text-sm text-red-600 flex items-center">
                                    <XCircle className="h-3 w-3 mr-1" />
                                    {errors.name}
                                </p>
                            )}
                        </div>

                        {/* Phone Field */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                                <Phone className="h-4 w-4 mr-2 text-blue-600" />
                                ফোন নম্বর *
                            </label>
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                onBlur={checkPhoneAvailability}
                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                                    errors.phone ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder="01XXXXXXXXX"
                                required
                            />
                            {errors.phone && (
                                <p className="mt-1 text-sm text-red-600 flex items-center">
                                    <XCircle className="h-3 w-3 mr-1" />
                                    {errors.phone}
                                </p>
                            )}
                        </div>

                        {/* Email Field (Optional) */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                                <Mail className="h-4 w-4 mr-2 text-blue-600" />
                                ইমেইল (ঐচ্ছিক)
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                                    errors.email ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder="আপনার ইমেইল ঠিকানা"
                            />
                            {errors.email && (
                                <p className="mt-1 text-sm text-red-600 flex items-center">
                                    <XCircle className="h-3 w-3 mr-1" />
                                    {errors.email}
                                </p>
                            )}
                        </div>

                        {/* Password Field */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                                <Lock className="h-4 w-4 mr-2 text-blue-600" />
                                পাসওয়ার্ড *
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                                        errors.password ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    placeholder="最少 ৮ অক্ষর"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            {errors.password && (
                                <p className="mt-1 text-sm text-red-600 flex items-center">
                                    <XCircle className="h-3 w-3 mr-1" />
                                    {errors.password}
                                </p>
                            )}
                        </div>

                        {/* Confirm Password Field */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                পাসওয়ার্ড নিশ্চিত করুন *
                            </label>
                            <div className="relative">
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    name="password_confirmation"
                                    value={formData.password_confirmation}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                                        errors.password_confirmation ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    placeholder="পাসওয়ার্ড再次输入"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            {errors.password_confirmation && (
                                <p className="mt-1 text-sm text-red-600 flex items-center">
                                    <XCircle className="h-3 w-3 mr-1" />
                                    {errors.password_confirmation}
                                </p>
                            )}
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-3 px-4 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    তৈরি হচ্ছে...
                                </>
                            ) : (
                                <>
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    একাউন্ট তৈরি করুন
                                </>
                            )}
                        </button>
                    </form>

                    {/* Login Link */}
                    <div className="mt-6 text-center">
                        <p className="text-gray-600">
                            Already have an account?{' '}
                            <Link
                                to="/login"
                                className="text-blue-600 hover:text-blue-800 font-semibold transition-colors"
                            >
                                Login here
                            </Link>
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 p-4 text-center border-t">
                    <p className="text-sm text-gray-600">
                        By registering, you agree to our Terms of Service and Privacy Policy
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Register;